import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, FileText, Loader2, Plus, RefreshCcw, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { createSourceSet, listUserSourceSets } from '../services/sourceSetService';
import type { SourceSet, SourceSetIntendedOutput } from '../types/sourceSet';

function formatBytes(bytes: number): string {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
}

function formatDate(value: any): string {
  if (!value) return 'Not available';

  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    return date.toLocaleDateString();
  } catch {
    return 'Not available';
  }
}

function getIntendedOutputLabel(value?: SourceSetIntendedOutput): string {
  if (value === 'summary') return 'Document Summary';
  if (value === 'course') return 'Create Learning';
  if (value === 'tutor') return 'Ask Source';
  return 'Not sure yet';
}

export function SourceSets() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sourceSets, setSourceSets] = useState<SourceSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [intendedOutput, setIntendedOutput] = useState<SourceSetIntendedOutput>('undecided');

  const loadSourceSets = useCallback(async () => {
    if (!user?.uid) return;

    setLoading(true);
    setError('');

    try {
      const data = await listUserSourceSets(user.uid);
      setSourceSets(data);
    } catch (err: any) {
      console.error('Failed to load Source Sets:', err);
      setError(err?.message || 'Failed to load Source Sets.');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadSourceSets();
  }, [loadSourceSets]);

  const handleCreateSourceSet = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!user?.uid) {
      setError('You must be logged in to create a Source Set.');
      return;
    }

    if (!title.trim()) {
      setError('Please enter a Source Set title.');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const sourceSetId = await createSourceSet({
        ownerId: user.uid,
        title,
        description,
        intendedOutput,
        visibility: 'private'
      });

      setTitle('');
      setDescription('');
      setIntendedOutput('undecided');
      navigate(`/source-sets/${sourceSetId}`);
    } catch (err: any) {
      console.error('Failed to create Source Set:', err);
      setError(err?.message || 'Failed to create Source Set.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-main text-text-main px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-semibold text-primary uppercase tracking-wide">Batch 4 Foundation</p>
            <h1 className="text-3xl font-bold text-text-main mt-2">Source Sets</h1>
            <p className="text-text-alt mt-2 max-w-3xl">
              First create or open a Source Set, then add documents and URLs to it. A Source Set groups trusted materials
              into one knowledge package for summaries, Create Learning, and Ask Source.
            </p>
          </div>

          <button
            onClick={loadSourceSets}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border-main hover:bg-bg-alt transition-colors"
          >
            <RefreshCcw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <form
            onSubmit={handleCreateSourceSet}
            className="lg:col-span-1 bg-bg-alt border border-border-main rounded-3xl p-6 shadow-xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-bold text-xl">Step 1: Create Source Set</h2>
                <p className="text-sm text-text-alt">Enter a Source Set title first. This activates the Create and Add Files button, then you can add files and URLs on the next page.</p>
              </div>
            </div>

            <label className="block text-sm font-semibold mb-2">Title</label>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Example: Procurement Compliance Training Pack"
              className="w-full mb-4 px-4 py-3 rounded-xl bg-bg-main border border-border-main focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <label className="block text-sm font-semibold mb-2">Description</label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional short description"
              rows={4}
              className="w-full mb-4 px-4 py-3 rounded-xl bg-bg-main border border-border-main focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <label className="block text-sm font-semibold mb-2">What do you want to do with it?</label>
            <select
              value={intendedOutput}
              onChange={(event) => setIntendedOutput(event.target.value as SourceSetIntendedOutput)}
              className="w-full mb-6 px-4 py-3 rounded-xl bg-bg-main border border-border-main focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="undecided">Not sure yet</option>
              <option value="summary">Document Summary</option>
              <option value="course">Create Learning</option>
              <option value="tutor">Ask Source</option>
            </select>

            <button
              type="submit"
              disabled={creating || !title.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-primary text-white font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              {creating ? 'Creating...' : 'Create and Add Files'}
            </button>

            <p className="text-xs text-text-alt mt-4 leading-relaxed">
              After creating the Source Set, Lumina will open the source page where you can add PDF, DOCX, PPTX files, or webpage URLs.
            </p>
          </form>

          <div className="lg:col-span-2">
            {loading ? (
              <div className="bg-bg-alt border border-border-main rounded-3xl p-10 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-text-alt">Loading Source Sets...</p>
              </div>
            ) : sourceSets.length === 0 ? (
              <div className="bg-bg-alt border border-border-main rounded-3xl p-10 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-text-alt" />
                <h3 className="text-xl font-bold mb-2">No Source Sets yet</h3>
                <p className="text-text-alt">
                  Create your first Source Set to group documents and URLs into one trusted knowledge package.
                </p>
              </div>
            ) : (
              <>
              <div className="mb-4 rounded-2xl border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm text-text-main font-semibold">Already created a Source Set?</p>
                <p className="text-xs text-text-alt mt-1">
                  Open it below to add files or URLs, review sources, or continue later.
                </p>
              </div>

              <div className="space-y-4">
                {sourceSets.map((sourceSet) => (
                  <Link
                    key={sourceSet.sourceSetId}
                    to={`/source-sets/${sourceSet.sourceSetId}`}
                    className="block bg-bg-alt border border-border-main rounded-3xl p-6 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                          <BookOpen className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-text-main">{sourceSet.title}</h3>
                          <p className="text-sm text-text-alt mt-1">
                            {sourceSet.description || 'No description provided.'}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-4">
                            <span className="text-xs px-3 py-1 rounded-full bg-bg-main border border-border-main">
                              {sourceSet.fileCount || 0} source(s)
                            </span>
                            <span className="text-xs px-3 py-1 rounded-full bg-bg-main border border-border-main">
                              {formatBytes(sourceSet.totalBytes || 0)}
                            </span>
                            <span className="text-xs px-3 py-1 rounded-full bg-bg-main border border-border-main">
                              {getIntendedOutputLabel(sourceSet.intendedOutput)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="text-left md:text-right flex flex-col gap-3 md:items-end">
                        <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                          {sourceSet.status}
                        </span>
                        <p className="text-xs text-text-alt">
                          Created: {formatDate(sourceSet.createdAt)}
                        </p>
                        <span className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition-opacity">
                          <Upload className="w-4 h-4" />
                          Open / Add Files
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
